import type { InspectionItem, ReportLanguage } from '../../types';
import type { KatalogSprache } from '../../services/mangelKatalog/typen';
import type { Vorbelegung } from '../../services/mangelKatalog/vorbelegung';
import { naechsteFreieTicketNummer, ticketNummerFuerNeuenEintrag } from '../../services/inspectionItemStatus';
import { katalogTexte } from '../mangelKatalog/texte';

/**
 * „Standardmangel per Torte“ am Desktop (Paket G, docs/mangelkatalog-plan.md
 * „Desktop / Tablet-Layout“ und „Liste“): reine Helfer für InspectionList,
 * InspectionFilterBar und InspectionItemCard.
 */

/** Dialog-Ziel „neuen Eintrag anlegen“; sonst trägt das Ziel die ID des Eintrags. */
export const KATALOG_NEU = 'neu';

/**
 * Katalog in allen Berichtssprachen (seit 28.09.): bei 'en' englisch, bei
 * 'de' und 'de/en' deutsch (katalogSprache).
 */
export const katalogAktivFuer = (language: ReportLanguage | undefined): boolean =>
    language === 'de' || language === 'en' || language === 'de/en';

/** „Standardmangel wählen“ bzw. „Standardmangel für #12 wählen“ (Nummer wie an der Karte: Informationen zeigen keine). */
export function katalogDialogTitel(ziel: string | null, items: readonly InspectionItem[], sprache: KatalogSprache = 'de'): string {
    const t = katalogTexte(sprache);
    if (!ziel || ziel === KATALOG_NEU) return t.dialogTitel;
    const item = items.find((i) => i.id === ziel);
    return item && item.status !== 'general' && item.ticketNumber != null
        ? t.dialogTitelFuer(item.ticketNumber)
        : t.dialogTitel;
}

/**
 * Neuer Eintrag aus einer Katalog-Auswahl — wie „Textvorschlag“
 * (handleAIApply) einen neuen Eintrag anlegt: fortlaufende Nummer über den
 * ganzen Bestand, Abschnitt aus dem aktiven Filter, Festgestellt am = Frist =
 * Begehungstag (`datum`, freimeldeDatum(reportDate)), Status offen, ohne Fotos.
 * Liefert die Vorbelegung eine Dringlichkeit (Frist oder Sofortmaßnahme), gilt
 * die. Freitext: nur die katalog*-Felder, sonst ein leerer Eintrag.
 */
export function neuerEintragAusKatalog(p: {
    id: string;
    items: readonly InspectionItem[];
    vorbelegung: Vorbelegung;
    datum: string;
    activeSectionId?: string | null;
}): InspectionItem {
    const f = p.vorbelegung.felder;
    const neu: InspectionItem = {
        id: p.id,
        ticketNumber: ticketNummerFuerNeuenEintrag('open', naechsteFreieTicketNummer(p.items)),
        title: f.title ?? '',
        location: '',
        observation: f.observation ?? '',
        regulation: f.regulation ?? '',
        responsiblePerson: '',
        deadline: f.deadline ?? p.datum,
        dateFound: p.datum,
        status: 'open',
        photos: [],
        sectionId: p.activeSectionId && p.activeSectionId !== 'all' ? p.activeSectionId : undefined,
        katalogKey: f.katalogKey,
        katalogKategorie: f.katalogKategorie,
    };
    if (f.immediateAction !== undefined) neu.immediateAction = f.immediateAction;
    return neu;
}

/**
 * Knopf „Standard“ an einer bestehenden Karte: Titel, Beschreibung, Vorschrift
 * und katalog*-Felder werden überschrieben; Frist/Sofortmaßnahme nur, wenn die
 * Vorbelegung sie liefert (sonst bleibt die Frist des Eintrags). Freitext:
 * nur die katalog*-Felder (Schlüssel null = bewusst gelöscht).
 */
export function katalogPatch(v: Vorbelegung): Partial<InspectionItem> {
    const f = v.felder;
    const patch: Partial<InspectionItem> = {
        katalogKey: f.katalogKey,
        katalogKategorie: f.katalogKategorie,
    };
    if (f.title !== undefined) patch.title = f.title;
    if (f.observation !== undefined) patch.observation = f.observation;
    if (f.regulation !== undefined) patch.regulation = f.regulation;
    if (f.deadline !== undefined) patch.deadline = f.deadline;
    if (f.immediateAction !== undefined) patch.immediateAction = f.immediateAction;
    return patch;
}

/**
 * Wirksamer Filter „Thema“: der gewählte, solange er unter den Themen der
 * Liste steht; sonst null (alle). Abgeleitet statt zurückgesetzt, damit
 * kein setState im Effekt nötig ist.
 */
export function wirksamesThema(themen: readonly { id: string }[], themaFilter: string | null): string | null {
    return themaFilter && themen.some((t) => t.id === themaFilter) ? themaFilter : null;
}

/**
 * Themenfilter mit Gedächtnis: `bekannt` sind die Einträge, die es beim Setzen
 * des Filters schon gab. Alles, was danach dazukommt („Neuer Eintrag“,
 * Textvorschlag, Handy-Assistent), bleibt sichtbar, auch ohne Kategorie —
 * sonst verschwände ein gerade angelegter Eintrag hinter dem Filter
 * (Codex-Review 27.09.). Rein abgeleitet, kein Zurücksetzen im Effekt.
 */
export function themaFilterAnwenden<T extends Pick<InspectionItem, 'id' | 'katalogKategorie'>>(
    items: readonly T[],
    themaId: string | null,
    bekannt: ReadonlySet<string> | null,
): T[] {
    if (!themaId) return [...items];
    return items.filter((i) => i.katalogKategorie === themaId || (bekannt !== null && !bekannt.has(i.id)));
}
